import { AlertStatus } from '@repo/shared/dtos/alert'
import { useT } from '@repo/ui/hooks'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { formatDate } from '@repo/ui/utils'
import { Archive, Bell, BellDot, MessagesSquare } from 'lucide-react'

import { AssistantMessage } from '@/components/assistant/messages/AssistantMessage'
import { BeneficiaryLink } from '@/components/beneficiary/BeneficiaryLink'
import { Link } from '@/components/Link'
import { useView } from '@/hooks'
import { storeActions } from '@/store/actions'
import { Alert as AlertType } from '@/store/alert'
import { Beneficiary } from '@/store/beneficiary'
import { useStoreDispatch } from '@/store/hooks'

export function Alert({ alert, beneficiary }: { alert: AlertType; beneficiary: Beneficiary }) {
  const dispatch = useStoreDispatch()
  const { t, tc } = useT({ keyPrefix: 'assistant.alert' })
  const view = useView()

  const markAsRead = () => {
    void dispatch(
      storeActions.assistant.alert.markAs.read({
        alertId: alert.id,
        beneficiaryId: alert.beneficiaryId,
      }),
    )
  }
  const markAsArchived = () => {
    void dispatch(
      storeActions.assistant.alert.markAs.archived({
        alertId: alert.id,
        beneficiaryId: alert.beneficiaryId,
      }),
    )
  }
  switch (alert.status) {
    case AlertStatus.UNREAD:
      return (
        <AssistantMessage className="shadow-primary-100/80 bg-highlight border-2 border-transparent bg-opacity-85 text-white">
          <div className="mb-2 flex select-none items-center gap-2 text-xl font-bold text-white">
            <BellDot className="h-7 w-7" /> {t('unreadAlert')}
          </div>

          <BeneficiaryLink
            id={beneficiary.id}
            fullName={beneficiary.fullName}
            className="mb-2 text-base text-white hover:text-white"
          />

          <div className="font-semibold">{alert.title}</div>
          <div className="mb-2 font-medium">{alert.content}</div>

          <Link
            className="mb-2 text-base text-white hover:text-white"
            onClick={view.beneficiary(alert.beneficiaryId).chat(alert.chatSessionId).navigate}
          >
            <MessagesSquare className="size-4" /> {t('goToChat')}
          </Link>

          <div className="inline select-none">
            <span className="mr-1 font-medium">{t('didYouReadIt')}</span>
            <div className="inline cursor-pointer underline" onClick={markAsRead}>
              {t('markAsRead')}
              {/* // TODO: trigger assistantprofilesummary to get tips about how to handle this alert */}
            </div>
          </div>
          <div className="text-primary-50 mt-2 select-none text-xs">
            {t('createdAt')} {formatDate(alert.createdAt)}
          </div>
        </AssistantMessage>
      )

    case AlertStatus.READ:
      return (
        <AssistantMessage>
          <div className="flex items-center justify-between gap-2">
            <div className="mb-1 flex select-none items-center gap-2 font-semibold">
              <Bell className="size-5" /> {t('readAlert')}
            </div>
            <TooltipWrapper content={tc('archive', { cfl: true })}>
              <Archive
                className="text-primary-400 size-4 cursor-pointer hover:text-white"
                onClick={markAsArchived}
              />
            </TooltipWrapper>
          </div>

          <BeneficiaryLink
            id={beneficiary.id}
            fullName={beneficiary.fullName}
            className="text-primary-200 hover:text-primary-50 mb-1 text-base"
          />

          <div className="font-semibold">{alert.title}</div>
          <div className="mb-2">{alert.content}</div>

          <Link
            className="text-primary-200 hover:text-primary-50 mb-2 text-base"
            onClick={view.beneficiary(alert.beneficiaryId).chat(alert.chatSessionId).navigate}
          >
            <MessagesSquare className="size-4" /> {t('goToChat')}
          </Link>

          <div className="text-primary-500 select-none text-xs">
            {t('markedAsRead')} {formatDate(alert.statusChangedAt)}
          </div>
        </AssistantMessage>
      )

    case AlertStatus.ARCHIVED:
      return (
        <div>
          <BeneficiaryLink id={beneficiary.id} fullName={beneficiary.fullName} className="mb-1" />

          <div className="font-semibold">{alert.title}</div>
          <div className="mb-2">{alert.content}</div>

          <Link
            className="mb-2"
            onClick={view.beneficiary(alert.beneficiaryId).chat(alert.chatSessionId).navigate}
          >
            <MessagesSquare className="size-4" /> {t('goToChat')}
          </Link>

          <div className="text-primary-600 select-none text-xs">
            {t('archivedAt')} {formatDate(alert.statusChangedAt)}
          </div>
        </div>
      )
  }
}
